import { TranslateService } from '@ngx-translate/core';
import { DeviceErrorType, DeviceState } from 'src/app/models/device-manger';
import { DeviceAndBrowserDetectorService } from 'src/app/services/device-and-browser-detector.service';
import { DeviceType } from '../devices-handle-util';

export interface DeviceErrorHelperStep {
  stepText: string;
  stepImgSrc?: string;
}

export enum Browser {
  CHROME = 'chrome',
  EDGE = 'edge',
  SAFARI = 'safari',
  FIREFOX = 'firefox',
  OPERA = 'opera',
}

export enum SupportedBrowserImg {
  CHROME = 'chrome',
  EDGE = 'edge',
  SAFARI = 'safari',
  FIREFOX = 'firefox',
}

export enum DeviceCategoryName {
  iOS = 'iOS',
  iPadOS = 'iPadOS',
  Android = 'Android',
  Tablet = 'Tablet',
  Windows = 'Windows',
  MacOS = 'MacOS',
}

export const STEP_WIDTH: number = 275;
export const GAP_BETWEEN_STEPS: number = 56;
export const PADDING_X: number = 32;
export const WIDE_MODAL_MIN_WIDTH = 750;

export abstract class DeviceErrorHelperModelBase {
  protected helperSupportedBrowsers: string[] = [
    Browser.CHROME,
    Browser.EDGE,
    Browser.SAFARI,
    Browser.FIREFOX,
  ];

  constructor(
    protected translateService: TranslateService,
    protected deviceDetector: DeviceAndBrowserDetectorService,
    protected deviceType: DeviceType,
  ) {}

  abstract getSteps(): DeviceErrorHelperStep[];
  abstract getMobileTitle(): string;
  abstract getDesktopTitle(): string;
  abstract hasRelatedError(cam: DeviceState, mic: DeviceState): boolean;
  abstract showRefreshButton(): boolean;
  abstract getErrorMsg(cam: DeviceState, mic: DeviceState): string;

  showStepperModal(): boolean {
    return !(this.canFitAllSteps() && this.contentIsWideEnough());
  }

  private canFitAllSteps(): boolean {
    const availableWidth = window.innerWidth * 0.8;

    const canFit = availableWidth - this.getAllStepsWidth() >= 0;

    return canFit;
  }

  private contentIsWideEnough(): boolean {
    const contentIsWideEnough = this.getAllStepsWidth() >= WIDE_MODAL_MIN_WIDTH;

    return contentIsWideEnough;
  }

  private getAllStepsWidth(): number {
    const steps = this.getSteps();
    return steps.length * STEP_WIDTH + GAP_BETWEEN_STEPS * (steps.length - 1) + PADDING_X * 2;
  }

  showDeviceSettingsButton() {
    return this.showRealDeviceSettingsButtonByGuide() && this.supportsDeviceSettingsDeepLink();
  }

  /** Label for the deep-link button in `app-steps-guide` (defaults to “System Settings”). */
  getDeviceSettingsButtonLabel(): string {
    return this.translateService.instant('System Settings');
  }

  // can be overwritten by sub classes to support device setting button
  deviceSettingButtonSrc(): string {
    return '';
  }

  // can be overwritten by sub classes to support device setting button
  protected showRealDeviceSettingsButtonByGuide(): boolean {
    return false;
  }

  protected BrowserDisplayName(): string {
    if (this.deviceDetector.isSafari()) {
      return 'Safari';
    }
    if (this.deviceDetector.isEdge()) {
      return 'Edge';
    }
    if (this.deviceDetector.isFirefox()) {
      return 'Firefox';
    }
    if (this.deviceDetector.isChrome()) {
      return 'Chrome';
    }
    return 'Chrome';
  }

  protected getBrowserImgName(browserName: string): string {
    const browserNameLowerCase = browserName.toLowerCase();
    return Object.values(SupportedBrowserImg).includes(browserNameLowerCase as SupportedBrowserImg)
      ? `${browserNameLowerCase}.png`
      : 'generic-browser.png';
  }

  /**
   * Whether this device class can use a settings URL from the helper footer (Windows / macOS desktop by default).
   * Permission flows on iPhone / iPad extend this via `isiOS()`.
   */
  protected supportsDeviceSettingsDeepLink(): boolean {
    return this.deviceDetector.isWindows() || this.deviceDetector.isMacOS();
  }

  hasErrorOfType(deviceState: DeviceState, errorType: DeviceErrorType): boolean {
    return deviceState.hasError && deviceState.errorType === errorType;
  }

  protected strongHtml(translatedText: string): string {
    return `<strong>${translatedText}</strong>`;
  }

  protected externalHelpLinkHtml(href: string, translatedLabel: string): string {
    return `<a style="color: #609ED8; text-decoration: underline; font-weight: 700;" target="_blank" rel="noopener noreferrer" href="${href}">${translatedLabel}</a>`;
  }
}
